import { getCookies } from '~/store/cookies';
import configuration from '~/configuration/configuration';
import { BaseResponse, HttpMethods, requestApi } from '~/services/api-new';
import { getGoogleClientId } from '../authentication';
import { reportException } from '@comeon/error-reporter';
import { ErrorReporterDomain } from '../error-handling/error-reporter-domain';

const API_BASE_PATH = process.env.API_BASE_PATH || '';

const getBaseUrl = (guestLoginProvider = 'Trustly') => {
	const providerIntegration = guestLoginProvider.includes('Integration')
		? guestLoginProvider
		: `${guestLoginProvider}Integration`;
	return `/${providerIntegration}/rest/verifyAccount`;
};

let baseUrl = getBaseUrl(configuration.guestLoginProvider);

export const guestLoginUtil = (
	data: {
		isMobileDevice: boolean;
		currencyISO?: string;
		amount?: string;
		personalNumber?: string;
		btag?: string | null;
		referrerUrl?: string;
		depositType?: number;
	},
	providerIntegration: string | null
) => {
	let url;
	if (providerIntegration) {
		url = `${API_BASE_PATH}${getBaseUrl(providerIntegration)}/startDepositLogin`;
	} else {
		baseUrl = getBaseUrl(configuration.guestLoginProvider);
		url = `${API_BASE_PATH}${baseUrl}/startVerifyAccount`;
	}
	return requestApi<{
		successful: boolean;
		externalPanelInformation?: {
			actualSendHandler: string;
			externalUrl: string;
			externalParameters: {
				method?: string;
			};
			headerTextKey: string;
			openExternalPanelFlow: string;
		};
		fetchResultUrl: string;
		verifyAccountRequestId: string;
		errorMessage: {
			info: string;
			title: string;
			message: string;
		};
	}>({
		endpoint: url,
		method: HttpMethods.POST,
		payload: { ...data, clientId: getGoogleClientId() as string | null }
	});
};

export const guestLoginSetDepositLimits = (data: { intRef: string; limits: Record<string, number> }) => {
	const url = `${API_BASE_PATH}${baseUrl}/setDepositLimits`;
	return requestApi({
		endpoint: url,
		method: HttpMethods.POST,
		payload: data
	});
};

export const guestLoginGetMethod = (methodUrl: string, data = {}, APIMethod = HttpMethods.GET) => {
	const selectedProviderIntegration = getCookies('providerIntegration');
	if (selectedProviderIntegration) {
		baseUrl = getBaseUrl(selectedProviderIntegration);
	}
	const url = `${API_BASE_PATH}${baseUrl}${methodUrl}`;
	return requestApi({
		endpoint: url,
		method: APIMethod,
		payload: data
	});
};

export const guestLoginUserInformation = (
	methodUrl: string,
	data: {
		intRef?: string;
		mobile?: string;
		mobileCountryCode?: string;
		acceptMarketingOffers?: string;
		email?: string;
		firstName?: string;
		lastName?: string;
		familyName?: string;
		password?: string;
		citizenship?: string;
		birthPlace?: string;
	} = {}
) => {
	const url = `${API_BASE_PATH}${baseUrl}${methodUrl}`;
	return requestApi<BaseResponse>({
		endpoint: url,
		method: HttpMethods.POST,
		payload: data
	});
};

// TrustlyIntegration/rest/verifyAccount/updateStatus
export const requestReopenTrustly = async (
	intRef: string,
	onReopening: () => void,
	onFailedReopen: (err?: string) => void,
	dataLayerReactivatedAccountEvent: () => void
) => {
	try {
		const resp = await guestLoginUserInformation('/updateStatus', {
			intRef
		});

		if (resp.status === 'SUCCESS') {
			onReopening();
			dataLayerReactivatedAccountEvent();
		} else {
			onFailedReopen(resp.errorDescription);
		}
	} catch {
		onFailedReopen();
	}
};

export const reLoginTrustly = async (intRef: string) => {
	try {
		await guestLoginUserInformation('/retry-login', {
			intRef
		});
	} catch (error) {
		reportException(
			new Error(`An error occurred while trying to call /retry-login`, {
				cause: error
			}),
			{ domain: ErrorReporterDomain.AUTHENTICATION }
		);
	}
};

export const reLoginCancelledTrustly = async (intRef: string) => {
	try {
		await guestLoginUserInformation('/retry-login-cancelled', {
			intRef
		});
	} catch (error) {
		reportException(
			new Error(`An error occurred while trying to call /retry-login-cancelled`, {
				cause: error
			}),
			{ domain: ErrorReporterDomain.AUTHENTICATION }
		);
	}
};
